import { useCallback, useEffect, useRef, useState } from "react";
import { isInteractiveTarget, wrapIndex } from "../utils/deck";

const DRAG_THRESHOLD = 48;
const WHEEL_THRESHOLD = 34;
const WHEEL_COOLDOWN = 420;

export function useHorizontalDeck({ itemCount, initialIndex = 0, onChange }) {
  const [activeIndex, setActiveIndex] = useState(() => wrapIndex(initialIndex, itemCount));
  const dragStart = useRef(null);
  const lastWheelAt = useRef(0);

  useEffect(() => {
    setActiveIndex((current) => wrapIndex(current, itemCount));
  }, [itemCount]);

  const goTo = useCallback(
    (nextIndex) => {
      setActiveIndex((current) => {
        const next = wrapIndex(
          typeof nextIndex === "function" ? nextIndex(current) : nextIndex,
          itemCount,
        );
        if (next !== current) onChange?.(next);
        return next;
      });
    },
    [itemCount, onChange],
  );

  const previous = useCallback(() => goTo((current) => current - 1), [goTo]);
  const next = useCallback(() => goTo((current) => current + 1), [goTo]);

  const onKeyDown = useCallback(
    (event) => {
      if (isInteractiveTarget(event.target)) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        previous();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        next();
      } else if (event.key === "Home") {
        event.preventDefault();
        goTo(0);
      } else if (event.key === "End") {
        event.preventDefault();
        goTo(itemCount - 1);
      }
    },
    [goTo, itemCount, next, previous],
  );

  const onWheel = useCallback(
    (event) => {
      if (isInteractiveTarget(event.target)) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const now = Date.now();
      if (Math.abs(delta) < WHEEL_THRESHOLD || now - lastWheelAt.current < WHEEL_COOLDOWN) return;

      event.preventDefault();
      lastWheelAt.current = now;
      delta > 0 ? next() : previous();
    },
    [next, previous],
  );

  const onPointerDown = useCallback((event) => {
    if (isInteractiveTarget(event.target)) return;
    dragStart.current = { id: event.pointerId, x: event.clientX };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, []);

  const onPointerUp = useCallback(
    (event) => {
      if (!dragStart.current || dragStart.current.id !== event.pointerId) return;
      const distance = event.clientX - dragStart.current.x;
      dragStart.current = null;
      if (Math.abs(distance) >= DRAG_THRESHOLD) distance < 0 ? next() : previous();
    },
    [next, previous],
  );

  const onPointerCancel = useCallback(() => {
    dragStart.current = null;
  }, []);

  return {
    activeIndex,
    goTo,
    previous,
    next,
    interactionProps: {
      onKeyDown,
      onWheel,
      onPointerDown,
      onPointerUp,
      onPointerCancel,
    },
  };
}
